import React, { useState, useEffect } from 'react';
import {
    Box,
    Grid,
    Card,
    CardContent,
    Typography,
    Button,
    Chip,
    IconButton,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Alert,
    CircularProgress,
    Paper,
    Avatar,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import {
    Assessment,
    AutoAwesome,
    Add,
    Edit,
    Delete,
    MoreVert,
    Visibility,
    Star,
    LocalLibrary
} from '@mui/icons-material';
import { useUser } from '../../contexts/UserContext';
import { useNavigate } from 'react-router-dom';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { handleFirestoreError } from '../../utils/errorHandling';
import {
    collection,
    query,
    where,
    orderBy,
    onSnapshot,
    doc,
    deleteDoc,
    updateDoc,
    Timestamp,
    Query,
    Unsubscribe
} from 'firebase/firestore';
import { db } from '../../firebaseClient';
import { GenerateSchemaDialog } from './GenerateSchemaDialog';


export interface RatingScale {
    type: string;
    min: number;
    max: number;
    labels: Record<number, string>;
}

export interface EvaluationCriteria {
    name: string;
    description: string;
    scoringMethodology: string;
    ratingScale: RatingScale;
    weight: number;
}

export interface EvaluationCategory {
    name: string;
    description: string;
    criteria: EvaluationCriteria[];
    weight: number;
}

export interface EvaluationSchema {
    id: string;
    name: string;
    description: string;
    version: string;
    isActive: boolean;
    isDefault: boolean;
    isGlobal: boolean;
    categories: EvaluationCategory[];
    tenantId?: string | null;
    createdAt: any;
    updatedAt: any;
    _collection?: string;
    createdBy?: any;
    updatedBy?: any;
}

const EvaluationSchemaList: React.FC = () => {
    const { user, userProfile, isTenantAdmin, isGlobalAdmin } = useUser();
    useDocumentTitle('Evaluation Frameworks');
    const navigate = useNavigate();

    const [schemas, setSchemas] = useState<EvaluationSchema[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
    const [selectedSchema, setSelectedSchema] = useState<EvaluationSchema | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [generateDialogOpen, setGenerateDialogOpen] = useState(false);

    useEffect(() => {
        if (user && userProfile) {
            loadSchemas();
        }
    }, [user, userProfile]);

    const loadSchemas = () => {
        try {
            setLoading(true);
            setError('');

            if (isGlobalAdmin()) {
                // Global admins see both tenant and global schemas
                const tenantSchemasQuery = query(
                    collection(db, 'evaluationSchemas'),
                    orderBy('createdAt', 'desc')
                );

                const globalSchemasQuery = query(
                    collection(db, 'globalEvaluationSchemas'),
                    where('isActive', '==', true),
                    orderBy('createdAt', 'desc')
                );

                // Listen to both collections
                let unsubscribeGlobal: Unsubscribe = () => { };

                const unsubscribeTenant = onSnapshot(tenantSchemasQuery, (tenantSnapshot) => {
                    const tenantSchemas: EvaluationSchema[] = [];
                    tenantSnapshot.forEach((doc) => {
                        tenantSchemas.push({ id: doc.id, ...(doc.data() as any), _collection: 'evaluationSchemas' } as EvaluationSchema);
                    });

                    unsubscribeGlobal = onSnapshot(globalSchemasQuery, (globalSnapshot) => {
                        const globalSchemas: EvaluationSchema[] = [];
                        globalSnapshot.forEach((doc) => {
                            globalSchemas.push({ id: doc.id, ...(doc.data() as any), _collection: 'globalEvaluationSchemas' } as EvaluationSchema);
                        });

                        // Combine and sort by creation date
                        const allSchemas = [...tenantSchemas, ...globalSchemas].sort((a, b) => {
                            const aTime = a.createdAt?.seconds || 0;
                            const bTime = b.createdAt?.seconds || 0;
                            return bTime - aTime;
                        });

                        setSchemas(allSchemas);
                        setLoading(false);
                    }, (error) => {
                        console.error('Error loading global schemas:', error);
                        handleFirestoreError(error, 'Loading global schemas', setError);
                        setLoading(false);
                    });
                }, (error) => {
                    console.error('Error loading tenant schemas:', error);
                    handleFirestoreError(error, 'Loading tenant schemas', setError);
                    setLoading(false);
                });

                return () => {
                    unsubscribeTenant();
                    unsubscribeGlobal();
                };
            } else {
                // Tenant users see only their tenant's schemas
                const schemasQuery = query(
                    collection(db, 'evaluationSchemas'),
                    where('tenantId', '==', user?.tenantId),
                    orderBy('createdAt', 'desc')
                );

                const unsubscribe = onSnapshot(schemasQuery, (snapshot) => {
                    const schemaList: EvaluationSchema[] = [];
                    snapshot.forEach((doc) => {
                        schemaList.push({ id: doc.id, ...(doc.data() as any), _collection: 'evaluationSchemas' } as EvaluationSchema);
                    });
                    setSchemas(schemaList);
                    setLoading(false);
                }, (error) => {
                    console.error('Error loading schemas:', error);
                    handleFirestoreError(error, 'Loading evaluation frameworks', setError);
                    setLoading(false);
                });

                return () => unsubscribe();
            }
        } catch (error) {
            console.error('Error setting up schema listener:', error);
            setError('Failed to load evaluation frameworks');
            setLoading(false);
        }
    };

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, schema: EvaluationSchema) => {
        setMenuAnchor(event.currentTarget);
        setSelectedSchema(schema);
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
        setSelectedSchema(null);
    };

    const handleViewSchema = () => {
        if (selectedSchema) {
            const isGlobalSchema = selectedSchema._collection === 'globalEvaluationSchemas';
            const viewPath = isGlobalSchema
                ? `/evaluation-schemas/global/${selectedSchema.id}`
                : `/evaluation-schemas/${selectedSchema.id}`;
            navigate(viewPath);
        }
        handleMenuClose();
    };

    const handleEditSchema = () => {
        if (selectedSchema) {
            const isGlobalSchema = selectedSchema._collection === 'globalEvaluationSchemas';
            const editPath = isGlobalSchema
                ? `/evaluation-schemas/global/${selectedSchema.id}/edit`
                : `/evaluation-schemas/${selectedSchema.id}/edit`;
            navigate(editPath);
        }
        handleMenuClose();
    };

    const handleDeleteSchema = async () => {
        if (!selectedSchema) return;

        try {
            const collectionName = selectedSchema._collection || 'evaluationSchemas';
            await deleteDoc(doc(db, collectionName, selectedSchema.id));
            setDeleteDialogOpen(false);
            handleMenuClose();
        } catch (error) {
            console.error('Error deleting schema:', error);
            setError('Failed to delete framework');
        }
    };

    const handleSetDefault = async () => {
        if (!selectedSchema || !isTenantAdmin()) return;

        try {
            // Update the selected schema as default
            await updateDoc(doc(db, 'evaluationSchemas', selectedSchema.id), {
                isDefault: true,
                updatedAt: Timestamp.now()
            });

            // Remove default flag from other schemas in this tenant
            const otherSchemas = schemas.filter(s => s.id !== selectedSchema.id && s.isDefault);
            for (const schema of otherSchemas) {
                await updateDoc(doc(db, 'evaluationSchemas', schema.id), {
                    isDefault: false,
                    updatedAt: Timestamp.now()
                });
            }

            handleMenuClose();
        } catch (error) {
            console.error('Error setting default framework:', error);
            setError('Failed to set default framework');
        }
    };

    const handleGenerateSuccess = (generatedSchema: any) => {
        navigate('/evaluation-schemas/new', { state: { generatedSchema } });
    };

    const canEditSchema = (schema: EvaluationSchema | null) => {
        if (!user || !schema) return false;

        // Global admins can edit any schema
        if (isGlobalAdmin()) return true;

        // Tenant admins can only edit their tenant's schemas (not global ones)
        return isTenantAdmin() && schema.tenantId === user.tenantId && !schema.isGlobal;
    };

    const formatDate = (timestamp: any) => {
        if (!timestamp) return '';
        return new Date(timestamp.seconds * 1000).toLocaleDateString();
    };

    const getCategoryCount = (schema: EvaluationSchema) => {
        return schema.categories ? schema.categories.length : 0;
    };

    const getCriteriaCount = (schema: EvaluationSchema) => {
        if (!schema.categories) return 0;
        return schema.categories.reduce((total, category) => {
            return total + (category.criteria ? category.criteria.length : 0);
        }, 0);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ flexGrow: 1 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Box>
                    <Typography variant="h4" gutterBottom>
                        Evaluation Frameworks
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Manage evaluation rubrics for presentation assessments
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                        variant="outlined"
                        startIcon={<LocalLibrary />}
                        onClick={() => navigate('/evaluation-schemas/global-library')}
                    >
                        Global Framework Library
                    </Button>
                    {(isTenantAdmin() || isGlobalAdmin()) && (
                        <>
                            <Button
                                variant="outlined"
                                startIcon={<AutoAwesome />}
                                onClick={() => setGenerateDialogOpen(true)}
                                sx={{
                                    borderColor: 'primary.main',
                                    color: 'primary.main',
                                    '&:hover': {
                                        borderColor: 'primary.dark',
                                        backgroundColor: 'rgba(25, 118, 210, 0.04)'
                                    }
                                }}
                            >
                                Generate with AI
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<Add />}
                                onClick={() => navigate('/evaluation-schemas/new')}
                            >
                                Create Framework
                            </Button>
                        </>
                    )}
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* Schema List */}
            {schemas.length === 0 ? (
                <Paper sx={{ p: 6, textAlign: 'center' }}>
                    <Avatar sx={{ bgcolor: 'grey.100', width: 64, height: 64, mx: 'auto', mb: 2 }}>
                        <Assessment />
                    </Avatar>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        No evaluation frameworks found
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
                        Create your first evaluation framework or browse the global framework library for ready-made templates
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                        <Button
                            variant="outlined"
                            startIcon={<LocalLibrary />}
                            onClick={() => navigate('/evaluation-schemas/global-library')}
                        >
                            Browse Global Framework Library
                        </Button>
                        {(isTenantAdmin() || isGlobalAdmin()) && (
                            <Button
                                variant="contained"
                                startIcon={<Add />}
                                onClick={() => navigate('/evaluation-schemas/new')}
                            >
                                Create Framework
                            </Button>
                        )}
                    </Box>
                </Paper>
            ) : (
                <Grid container spacing={3}>
                    {schemas.map((schema) => (
                        <Grid size={{ xs: 12, md: 6, lg: 4 }} key={schema.id}>
                            <Card
                                sx={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    border: schema.isDefault ? '2px solid' : '1px solid',
                                    borderColor: schema.isDefault ? 'primary.main' : 'divider',
                                }}
                            >
                                <CardContent sx={{ flexGrow: 1 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                <Typography
                                                    variant="h6"
                                                    sx={{
                                                        fontWeight: 600,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }}
                                                >
                                                    {schema.name}
                                                </Typography>
                                                {schema.isDefault && (
                                                    <Chip
                                                        icon={<Star />}
                                                        label="Default"
                                                        size="small"
                                                        color="primary"
                                                        variant="outlined"
                                                    />
                                                )}
                                            </Box>
                                            {schema.description && (
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: 'vertical',
                                                        mb: 2
                                                    }}
                                                >
                                                    {schema.description}
                                                </Typography>
                                            )}
                                        </Box>
                                        <IconButton
                                            size="small"
                                            onClick={(e) => handleMenuOpen(e, schema)}
                                        >
                                            <MoreVert />
                                        </IconButton>
                                    </Box>

                                    <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                                        <Chip
                                            label={`${getCategoryCount(schema)} Categories`}
                                            size="small"
                                            variant="outlined"
                                        />
                                        <Chip
                                            label={`${getCriteriaCount(schema)} Criteria`}
                                            size="small"
                                            variant="outlined"
                                        />
                                        {schema.isGlobal && (
                                            <Chip
                                                label="Global"
                                                size="small"
                                                color="info"
                                                variant="outlined"
                                            />
                                        )}
                                    </Box>

                                    <Divider sx={{ my: 2 }} />

                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="caption" color="text.secondary">
                                            Created: {formatDate(schema.createdAt)}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Version: {schema.version || '1.0'}
                                        </Typography>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* Action Menu */}
            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleMenuClose}
            >
                <MenuItem onClick={handleViewSchema}>
                    <ListItemIcon>
                        <Visibility fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>View Details</ListItemText>
                </MenuItem>

                {canEditSchema(selectedSchema) && (
                    <MenuItem onClick={handleEditSchema}>
                        <ListItemIcon>
                            <Edit fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Edit</ListItemText>
                    </MenuItem>
                )}

                {isTenantAdmin() && selectedSchema && !selectedSchema.isDefault && !selectedSchema.isGlobal && (
                    <MenuItem onClick={handleSetDefault}>
                        <ListItemIcon>
                            <Star fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Set as Default</ListItemText>
                    </MenuItem>
                )}

                {canEditSchema(selectedSchema) && (
                    <MenuItem
                        onClick={() => setDeleteDialogOpen(true)}
                        sx={{ color: 'error.main' }}
                    >
                        <ListItemIcon>
                            <Delete fontSize="small" color="error" />
                        </ListItemIcon>
                        <ListItemText>Delete</ListItemText>
                    </MenuItem>
                )}
            </Menu>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Delete Evaluation Framework</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete "{selectedSchema?.name}"? This action cannot be undone.
                    </Typography>
                    {selectedSchema?.isDefault && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                            This is the default framework. You should set another framework as default before deleting this one.
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleDeleteSchema} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            <GenerateSchemaDialog
                open={generateDialogOpen}
                onClose={() => setGenerateDialogOpen(false)}
                onSuccess={handleGenerateSuccess}
                isGlobalMode={false} // Always false here, creating for tenant
            />
        </Box >
    );
};

export default EvaluationSchemaList;
